import { AriaAttributes } from 'react';
import './typography.scss';
import classNames from 'classnames';

export enum TypographyTypes {
  'display-2xl' = 'display-2xl',
  'display-xl' = 'display-xl',
  'display-lg' = 'display-lg',
  'display-md' = 'display-md',
  'display-sm' = 'display-sm',
  'display-xs' = 'display-xs',
  'text-xl' = 'text-xl',
  'text-lg' = 'text-lg',
  'text-md' = 'text-md',
  'text-sm' = 'text-sm',
  'text-xs' = 'text-xs',
}

export enum TypographyWeight {
  'regular' = 'regular',
  'medium' = 'medium',
  'semi-bold' = 'semi-bold',
  'bold' = 'bold',
}

interface TypographyProps {
  type: keyof typeof TypographyTypes;
  weight?: keyof typeof TypographyWeight;
  className?: string;
  style?: React.HTMLAttributes<any>;
  dataCy?: string;
}

export const Typography: React.FC<React.PropsWithChildren<TypographyProps & AriaAttributes>> = ({
  type,
  className,
  weight = 'regular',
  style,
  children,
  dataCy,
  ...ariaAttributes
}: React.PropsWithChildren<TypographyProps>) => {
  const getTypographyElem = (type: string, children: any) => {
    let textElem: any = null;

    switch (type) {
      case TypographyTypes['display-2xl']:
        textElem = (
          <h1
            data-cy={dataCy}
            className={classNames('quipli-v2', 'h1', `${weight}`, className)}
            {...style}
            {...ariaAttributes}
          >
            {children}
          </h1>
        );
        break;
      case TypographyTypes['display-xl']:
        textElem = (
          <h2
            data-cy={dataCy}
            className={classNames('quipli-v2', 'h2', `${weight}`, className)}
            {...style}
            {...ariaAttributes}
          >
            {children}
          </h2>
        );
        break;
      case TypographyTypes['display-lg']:
        textElem = (
          <h3
            data-cy={dataCy}
            className={classNames('quipli-v2', 'h3', `${weight}`, className)}
            {...style}
            {...ariaAttributes}
          >
            {children}
          </h3>
        );
        break;
      case TypographyTypes['display-md']:
        textElem = (
          <h4
            data-cy={dataCy}
            className={classNames('quipli-v2', 'h4', `${weight}`, className)}
            {...style}
            {...ariaAttributes}
          >
            {children}
          </h4>
        );
        break;
      case TypographyTypes['display-sm']:
        textElem = (
          <h5
            data-cy={dataCy}
            className={classNames('quipli-v2', 'h5', `${weight}`, className)}
            {...style}
            {...ariaAttributes}
          >
            {children}
          </h5>
        );
        break;
      case TypographyTypes['display-xs']:
        textElem = (
          <h6
            data-cy={dataCy}
            className={classNames('quipli-v2', 'h6', `${weight}`, className)}
            {...style}
            {...ariaAttributes}
          >
            {children}
          </h6>
        );
        break;

      case TypographyTypes['text-xl']:
        textElem = (
          <p
            data-cy={dataCy}
            className={classNames('quipli-v2', 'text-xl', `${weight}`, className)}
            {...style}
            {...ariaAttributes}
          >
            {children}
          </p>
        );
        break;
      case TypographyTypes['text-lg']:
        textElem = (
          <p
            data-cy={dataCy}
            className={classNames('quipli-v2', 'text-lg', `${weight}`, className)}
            {...style}
            {...ariaAttributes}
          >
            {children}
          </p>
        );
        break;
      case TypographyTypes['text-md']:
        textElem = (
          <p
            data-cy={dataCy}
            className={classNames('quipli-v2', 'text-md', `${weight}`, className)}
            {...style}
            {...ariaAttributes}
          >
            {children}
          </p>
        );
        break;
      case TypographyTypes['text-sm']:
        textElem = (
          <p
            data-cy={dataCy}
            className={classNames('quipli-v2', 'text-sm', `${weight}`, className)}
            {...style}
            {...ariaAttributes}
          >
            {children}
          </p>
        );
        break;
      case TypographyTypes['text-xs']:
        textElem = (
          <p
            data-cy={dataCy}
            className={classNames('quipli-v2', 'text-xs', `${weight}`, className)}
            {...style}
            {...ariaAttributes}
          >
            {children}
          </p>
        );
        break;

      default:
        textElem = children;
        break;
    }

    return textElem;
  };

  return getTypographyElem(type, children);
};
